import { useState, useCallback, type FormEvent } from 'react';

interface NewsletterSectionProps {
  formUrl: string;
}

export default function NewsletterSection({ formUrl }: NewsletterSectionProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const formEl = e.currentTarget;
      const formData = new FormData(formEl);

      const honeypot = (formData.get('website_alt') as string || '').trim();
      if (honeypot) {
        setStatus('success');
        setEmail('');
        return;
      }

      setStatus('loading');
      setErrorMsg('');

      try {
        const res = await fetch(formUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams(
            Array.from(formData.entries())
              .filter(([key]) => key !== 'website_alt')
              .map(([key, value]) => [key, value.toString()])
          ).toString(),
        });

        const responseText = await res.text();
        let parsed;
        try { parsed = JSON.parse(responseText); } catch { parsed = null; }

        if (res.ok && parsed?.code === 'OK') {
          setStatus('success');
          setEmail('');
        } else {
          const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '订阅失败，请稍后再试';
          setErrorMsg(serverMsg);
          setStatus('error');
        }
      } catch {
        setErrorMsg('网络错误，请检查网络连接后重试');
        setStatus('error');
      }
    },
    [formUrl],
  );

  return (
    <section className="w-full px-4 md:px-6 py-12 md:py-16 relative overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=A%20warm%20abstract%20geometric%20pattern%20with%20soft%20gradients%20in%20coral%20and%20amber%20tones%2C%20elegant%20minimal%20design%2C%20subtle%20texture%2C%20artistic%20background%20for%20a%20subscription%20section%2C%20clean%20and%20modern%20aesthetic%2C%20no%20text&width=1600&height=600&seq=newsletter-bg-2026&orientation=landscape"
        alt=""
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-primary-600/90 to-accent-600/85"></div>

      <div className="relative z-10 max-w-2xl mx-auto text-center">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">
          订阅每日同人精选
        </h2>
        <p className="text-sm md:text-base text-background-50/85 mb-6 leading-relaxed">
          每天一封邮件，为你精选当日最值得看的同人作品，不错过任何精彩
        </p>

        {status === 'success' ? (
          <div className="bg-background-50/20 backdrop-blur-sm rounded-lg p-5 text-background-50">
            <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-background-50/20">
              <i className="ri-check-line text-2xl"></i>
            </div>
            <p className="font-heading font-semibold text-lg mb-1">订阅成功！</p>
            <p className="text-sm text-background-50/80">感谢你的订阅，明天见！</p>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            data-readdy-form
            className="flex flex-col sm:flex-row items-center gap-3 max-w-md mx-auto"
          >
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="输入你的邮箱地址"
              required
              className="w-full sm:flex-1 px-4 py-3 rounded-lg bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 border border-transparent focus:outline-none focus:ring-2 focus:ring-background-50/50"
            />
            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="honeypot-field"
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full sm:w-auto px-6 py-3 bg-foreground-900 text-background-50 font-medium rounded-lg hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer text-sm disabled:opacity-60"
            >
              {status === 'loading' ? '订阅中...' : '立即订阅'}
            </button>
          </form>
        )}

        {status === 'error' && errorMsg && (
          <p className="mt-3 text-sm text-background-50/90 bg-red-500/20 rounded-lg px-4 py-2 inline-block">
            {errorMsg}
          </p>
        )}
      </div>
    </section>
  );
}